import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { Actions, Select, ofActionErrored, ofActionSuccessful } from '@ngxs/store';
import { TranslateService } from '@ngstack/translate';
import { Observable } from 'rxjs';
import * as utils from '@koomzo/commonutil';
import { normalizeMemberPhone, phoneRefusal } from '@koomzo/commonmodel';
import { LogIn } from '@common/auth/store/auth.actions';
import { UserRoleBasedRedirectService } from '@common/auth/services/user-rolebased-redirect.service';
import { BaseFormComponent } from '@shared/components/core/base-form.component';
import { BACKEND_ACTIONS_PORT, BackendActionsPort } from '@shared/services/backend';
import { AppState } from '@shared/store/app.state';
import { ChangeAppLang, SetCurrentTenant } from '@shared/store/app.actions';
import { AUTH_LANGUAGES } from '../login/login.page';
import { KZ_DESKTOP_WEBSITE, desktopWebsiteFallback } from '../desktop-website';

/**
 * "What do you run?" (specs/auth.md §5.3). The DS's eight kinds, plus restaurant, salon, gym and
 * grocery. The DS offers no kind for four of its own modules; they were added by ruling Q7 and are
 * a recorded divergence. The server maps each kind to its starting apps
 * (functions/.../signup-business-kinds.json).
 */
export const SIGNUP_BUSINESS_KINDS = [
  'choose', 'shop', 'bank', 'insurance', 'hotel', 'clinic', 'workshop', 'services',
  'restaurant', 'salon', 'gym', 'grocery'
] as const;

export const SIGNUP_HEADCOUNTS = ['justMe', 'twoToFive', 'sixToTwenty', 'twentyPlus'] as const;

/** Why the last submit failed, or null. */
export type SignupFailure = 'exists' | 'nameTaken' | 'phone' | 'appCheck' | 'generic' | null;

/**
 * `/signup`: create a workspace (kz2 F1).
 *
 * The LAYOUT is the design system's (koomzodesignsystem `features/auth/pages/signup`): the wide
 * slot-based shell, two field groups, the terms line, the two reassurances, and the dock.
 *
 * The BEHAVIOUR is koomzoapps'. The DS screen is a documented no-op; here, one `createWorkspace`
 * callable (BackendActionsPort, ruling Q7) creates the account, tenant, owner profile and starting
 * apps. The page then signs in through `LogIn` → `AUTH_GATEWAY_PORT`, and follows the role redirect
 * as Login does.
 *
 * The form is a dynamic-forms model (`CommonFormModels.getWorkspaceSignupFormModel`). Signing up
 * as a person rather than a business is `/signup/personal`, linked under the form: the legacy
 * personal signup, kept because consumers still sign up (`appMode: 'all'`).
 */
@Component({
  selector: 'app-signup',
  templateUrl: './signup.page.html',
  styleUrls: ['./signup.page.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class SignupPage extends BaseFormComponent<any> implements OnInit {
  readonly languages = AUTH_LANGUAGES;

  @Select(AppState.AppLang)
  lang$: Observable<string>;

  failure: SignupFailure = null;

  /** kzd-ac wave-2 review M4: the website's signup, when this desktop was refused for want of an App Check token. */
  websiteUrl: string | null = null;

  private readonly cdr = inject(ChangeDetectorRef);
  private readonly translate = inject(TranslateService);
  private readonly backendActions = inject<BackendActionsPort>(BACKEND_ACTIONS_PORT);
  private readonly loginRedirectService = inject(UserRoleBasedRedirectService);
  private readonly actions = inject(Actions);

  constructor() {
    super();
    this.title = 'Sign up';
  }

  ngOnInit(): void {
    this.buildForm();
    // Rebuild the copy once the new language's strings have loaded (see LoginPage).
    this.translate.activeLangChanged.subscribe(() => {
      this.buildForm();
      this.cdr.markForCheck();
    });

    this.actions.pipe(ofActionSuccessful(LogIn)).subscribe(() => {
      this.loaderService.hideLoader();
      if (this.currentUser?.tenantId) {
        this.store.dispatch(new SetCurrentTenant({ id: this.currentUser.tenantId }));
      }
      this.loginRedirectService.redirectUser(this.appEnv, this.navCtrl, this.currentUser, this.userRoleRouteRedirectPathMappingList);
    });
    this.actions.pipe(ofActionErrored(LogIn)).subscribe(() => this.fail('generic'));
  }

  ionViewWillEnter(): void {
    this.isLoadingState = false;
    this.menuCtrl.enable(false);
  }

  ionViewWillLeave(): void {
    this.menuCtrl.enable(true);
    this.isLoadingState = false;
  }

  onLanguageChange(code: string): void {
    if (code === 'en' || code === 'fr') {
      this.store.dispatch(new ChangeAppLang(code));
    }
  }

  async onSubmit(): Promise<void> {
    this.failure = null;
    this.websiteUrl = null;
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      this.cdr.markForCheck();
      return;
    }
    const v = this.form.value;
    /* The callable's phone rule, said before the call (kz2 accounts): `+<country><number>`, +237 = 9 digits, no 0. */
    if (!utils.isEmail(v.username) && phoneRefusal(normalizeMemberPhone(v.username))) {
      this.fail('phone');
      return;
    }
    this.isLoadingState = true;
    this.loaderService.showLoader(this.languageService.get('uiString.connecting'));
    try {
      await this.backendActions.createWorkspace({
        phone: v.username,
        password: v.password,
        email: v.email || undefined,
        businessName: v.businessName,
        fullName: v.fullName,
        businessKind: v.businessKind,
        headcount: v.headcount,
        language: this.languageService.activeLang,
        domain: this.appDomain
      });
    } catch (e: any) {
      /* kzd-ac review M4: the desktop has no token signup takes — send the person to the website instead. */
      const website = desktopWebsiteFallback(e, 'signup');
      if (website) {
        this.websiteUrl = website;
        this.fail('appCheck');
        return;
      }
      // createWorkspace refuses a taken phone (`already-exists`, 'phone') or business name ('businessName').
      const taken = String(e?.code ?? '').endsWith('already-exists');
      const badPhone = String(e?.code ?? '').endsWith('invalid-argument') && e?.message === 'phone';
      this.fail(badPhone ? 'phone' : !taken ? 'generic' : e?.message === 'businessName' ? 'nameTaken' : 'exists');
      return;
    }
    const username = utils.isEmail(v.username) ? v.username : utils.formatUsernameToEmailFormat(v.username, this.appDomain);
    this.store.dispatch(new LogIn({ username, password: v.password }));
  }

  /** Opens the website's signup in the system browser (review M4). */
  openWebsite(): void {
    if (this.websiteUrl) KZ_DESKTOP_WEBSITE.open(this.websiteUrl);
  }

  private fail(reason: SignupFailure): void {
    this.failure = reason;
    this.isLoadingState = false;
    this.loaderService.hideLoader();
    this.cdr.markForCheck();
  }

  /** (Re)builds the dynamic form in the active language, keeping whatever was typed. */
  private buildForm(): void {
    const t = (key: string) => this.languageService.get(key);
    const previous = this.form?.getRawValue();
    this.formModel = this.commonFormModels.getWorkspaceSignupFormModel(
      SIGNUP_BUSINESS_KINDS.map((k) => ({ value: k, label: t(`app.pages.auth.signup.field.kind.option.${k}`) })),
      SIGNUP_HEADCOUNTS.map((h) => ({ value: h, label: t(`app.pages.auth.signup.field.team.option.${h}`) }))
    );
    this.modelMap = this.dynamicFormsService.getformModelMap(this.formModel);
    this.form = this.dynamicFormsService.createFormGroup(this.formModel);
    this.form.patchValue(previous ?? { businessKind: 'choose', headcount: 'justMe', termsAccepted: true });
  }
}
